import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import ProductCard from "@/components/site/ProductCard";
import { Reveal } from "@/components/site/Reveal";

const CATEGORY_TABS = [
  { key: "", label: "All" },
  { key: "mens", label: "Men's" },
  { key: "womens", label: "Women's" },
  { key: "unisex", label: "Unisex" },
];

const SORTS = [
  { key: "newest", label: "Newest" },
  { key: "price_asc", label: "Price: Low to High" },
  { key: "price_desc", label: "Price: High to Low" },
  { key: "bestselling", label: "Bestselling" },
];

const PRICES = [
  { key: "", label: "Any Price" },
  { key: "under20", label: "Under $20" },
  { key: "20to50", label: "$20 – $50" },
  { key: "over50", label: "Over $50" },
];

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("newest");
  const [price, setPrice] = useState("");
  const [size, setSize] = useState(searchParams.get("format") === "rollon" ? "Roll-On" : "");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const category = searchParams.get("category") || "";

  useEffect(() => {
    setLoading(true);
    api.get("/products", { params: category ? { category } : {} })
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category]);

  const sizeOptions = useMemo(() => {
    const set = new Set();
    products.forEach((p) => p.sizes.forEach((s) => set.add(s.label)));
    return Array.from(set);
  }, [products]);

  const filtered = useMemo(() => {
    let list = [...products];
    if (featuredOnly) list = list.filter((p) => p.featured);
    if (size) list = list.filter((p) => p.sizes.some((s) => s.label === size && s.stock > 0));
    if (price === "under20") list = list.filter((p) => Math.min(...p.sizes.map((s) => s.price)) < 20);
    if (price === "20to50") list = list.filter((p) => Math.min(...p.sizes.map((s) => s.price)) >= 20 && Math.min(...p.sizes.map((s) => s.price)) <= 50);
    if (price === "over50") list = list.filter((p) => Math.min(...p.sizes.map((s) => s.price)) > 50);
    const minPrice = (p) => (p.sizes.length ? Math.min(...p.sizes.map((s) => s.price)) : 0);
    if (sort === "price_asc") list.sort((a, b) => minPrice(a) - minPrice(b));
    if (sort === "price_desc") list.sort((a, b) => minPrice(b) - minPrice(a));
    if (sort === "bestselling") list.sort((a, b) => (b.sales_count || 0) - (a.sales_count || 0));
    if (sort === "newest") list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return list;
  }, [products, sort, price, size, featuredOnly]);

  return (
    <div className="pt-20" data-testid="shop-page">
      <div className="bg-[#0A0A0A] gold-hairline-b py-16 sm:py-24 relative grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 text-center">
          <Reveal>
            <p className="eyebrow mb-4">The Collection</p>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white font-light" data-testid="shop-title">
              {category === "mens" ? "Men's Fragrances" : category === "womens" ? "Women's Fragrances" : category === "unisex" ? "Unisex Fragrances" : "Shop All Fragrances"}
            </h1>
          </Reveal>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-12">
        {/* Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-6 justify-between pb-10 border-b border-[#D4AF37]/10" data-testid="shop-filters">
          <div className="flex flex-wrap gap-2">
            {CATEGORY_TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => { setSearchParams(t.key ? { category: t.key } : {}); }}
                className={`font-sans-body text-[11px] uppercase tracking-[0.2em] px-4 py-2 border transition-colors ${
                  category === t.key ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-[#D4AF37]/20 text-[#B8B8B8] hover:border-[#D4AF37]/50"
                }`}
                data-testid={`filter-category-${t.key || "all"}`}
              >
                {t.label}
              </button>
            ))}
            <button
              onClick={() => setFeaturedOnly(!featuredOnly)}
              className={`font-sans-body text-[11px] uppercase tracking-[0.2em] px-4 py-2 border transition-colors ${
                featuredOnly ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-[#D4AF37]/20 text-[#B8B8B8] hover:border-[#D4AF37]/50"
              }`}
              data-testid="filter-featured-toggle"
            >
              Featured
            </button>
          </div>
          <div className="flex flex-wrap gap-3">
            <select value={size} onChange={(e) => setSize(e.target.value)} data-testid="filter-size-select"
              className="bg-[#0D0D0D] border border-[#D4AF37]/20 text-[#B8B8B8] font-sans-body text-xs px-3 py-2.5 focus:outline-none focus:border-[#D4AF37]">
              <option value="">Any Size</option>
              {sizeOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={price} onChange={(e) => setPrice(e.target.value)} data-testid="filter-price-select"
              className="bg-[#0D0D0D] border border-[#D4AF37]/20 text-[#B8B8B8] font-sans-body text-xs px-3 py-2.5 focus:outline-none focus:border-[#D4AF37]">
              {PRICES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-select"
              className="bg-[#0D0D0D] border border-[#D4AF37]/20 text-[#B8B8B8] font-sans-body text-xs px-3 py-2.5 focus:outline-none focus:border-[#D4AF37]">
              {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mt-12">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-[#0D0D0D] gold-hairline animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center font-sans-body text-[#B8B8B8] py-24" data-testid="shop-empty">
            No fragrances match your filters yet.
          </motion.p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mt-12">
            {filtered.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i}
                imageOverride={size.toLowerCase().includes("roll") ? p.rollon_image_url : ""} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
